export type HttpVerb = "GET" | "POST" | "PUT" | "DELETE";

export class HttpApiRequestOptions {
    /**
     * Skips the normal login redirect when a 401 is encountered. Critical for login and login check requests
     */
    ignoreAuthenticationErrors?: boolean;

    /**
     * Both the FormData to be posted to the given URL and the indication that this call will post FormData
     */
    formData?: FormData;
    additionalHeaders?: { [key: string]: string; };
    constructor() {
        this.ignoreAuthenticationErrors = false;
        this.formData = null;
    }
}

export interface HttpApiResult<T> {
    headers: object;
    ok: boolean;
    redirected: boolean;
    status: number;
    statusText: string;

    data: T;
}

export default class HttpApi {
    protected static readonly baseUrl = "/";

    public static Get<TResult>(relativeUrl: string, requestOptions: HttpApiRequestOptions) {
        return HttpApi.baseRequest<{}, TResult>({}, relativeUrl, "GET", requestOptions);
    }

    public static Post<TRequest, TResult>(req: TRequest, relativeUrl: string, requestOptions: HttpApiRequestOptions) {
        return HttpApi.baseRequest<TRequest, TResult>(req, relativeUrl, "POST", requestOptions);
    }

    public static Put<TRequest, TResult>(req: TRequest, relativeUrl: string, requestOptions: HttpApiRequestOptions) {
        return HttpApi.baseRequest<TRequest, TResult>(req, relativeUrl, "PUT", requestOptions);
    }

    public static Delete<TRequest, TResult>(req: TRequest, relativeUrl: string, requestOptions: HttpApiRequestOptions) {
        return HttpApi.baseRequest<TRequest, TResult>(req, relativeUrl, "DELETE", requestOptions);
    }

    protected static baseInfo<TRequest>(req: TRequest, method: HttpVerb, requestOptions: HttpApiRequestOptions ): RequestInit {
        if (requestOptions.formData != null) {
            return {
                method: method,
                body: requestOptions.formData,
            };
        }
        var requestInfo: RequestInit = {
            method: method,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Cache': 'no-cache'
            },
            credentials: 'include'
        };

        requestInfo.headers = { ...requestInfo.headers, ...requestOptions.additionalHeaders };

        if (method !== "GET") {
            requestInfo.body = JSON.stringify(req);
        }

        return requestInfo;
    }

    protected static baseRequest<TRequest, TResult>(req: TRequest, relativeUrl: string, method: HttpVerb, requestOptions: HttpApiRequestOptions = new HttpApiRequestOptions()): Promise<HttpApiResult<TResult>> {
        let info = HttpApi.baseInfo(req, method, requestOptions);
        let url = HttpApi.baseUrl + relativeUrl;

        return fetch(url, info)
            .then(response => {
                let headers = {};
                response.headers.forEach((value, key) => headers[key] = value);
                let actionResult: HttpApiResult<TResult> = {
                    headers: headers,
                    ok: response.ok,
                    redirected: response.redirected,
                    status: response.status,
                    statusText: response.statusText,
                    data: {} as TResult,
                };

                if (response.ok) {
                    return response.text().then((text: string) => {
                        if (text != null && text.length > 1) {
                            actionResult.data = JSON.parse(text);
                        }
                        return actionResult;
                    });
                }

                if (response.status === 401) {
                    if (!requestOptions.ignoreAuthenticationErrors) {
                        window.location.assign(`${location.protocol}//${location.host}/login`); // Unauthorized!
                    }
                }

                return actionResult;
            })
            .catch(e => {
                // Network errors popup here
                throw new Error(e.message);
            });
    }

    public static RestRequest<TRequest, TResult>(req: TRequest, requestVerb: string, relativeUrl: string, requestOptions: HttpApiRequestOptions) {
        requestOptions = requestOptions || {};
        switch (requestVerb.toUpperCase()) {
            case "GET":
                return HttpApi.Get<TResult>(relativeUrl, requestOptions);
            case "POST":
                return HttpApi.Post<TRequest, TResult>(req, relativeUrl, requestOptions);
            case "DELETE":
                return HttpApi.Delete<TRequest, TResult>(req, relativeUrl, requestOptions);
            case "PUT":
                return HttpApi.Put<TRequest, TResult>(req, relativeUrl, requestOptions);
            default:
                throw new Error(`Verb '${requestVerb}' is not valid`);
        }
    }
}



// WEBPACK FOOTER //
// ./src/api/HttpApi.ts